import React, { Component, useRef, useEffect, useState } from 'react'
import ReactPlayer from 'react-player';
import { Grid, Box, Container, Typography, Pagination } from '@mui/material'
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import AudioPlayer from 'react-h5-audio-player'
import 'react-h5-audio-player/lib/styles.css';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';


let Sound = (data)=>{
  const audioRef = useRef(null);
  const { registerAudioElement, unregisterAudioElement, playAudio, stopAudio, pauseAudio, currentPlayingId } = useAudioPlayer();
  const [isReady, setIsReady] = useState(false); // 再生準備ができているか
  
  // ユニークなIDを生成（itemのURLをベースに）
  const audioId = data?.item ? `audio_${data.item.replace(/[^a-zA-Z0-9]/g, '_')}` : null;


  useEffect(() => {
    if (audioId) {
      // AudioPlayerの内部のaudio要素を取得して登録
      const registerAudio = () => {
        if (audioRef.current && audioRef.current.audio && audioRef.current.audio.current) {
          registerAudioElement(audioId, audioRef.current.audio.current);
        }
      };
      
      // 少し遅延してから登録（コンポーネントがマウントされた後）
      const timer = setTimeout(registerAudio, 100);
      
      return () => {
        clearTimeout(timer);
        unregisterAudioElement(audioId);
      };
    }
  }, [audioId]);

  // 再生ボタンが押された時に再生を開始
  const handlePlay = () => {
    // console.log('[handlePlay] 再生ボタンが押されました', { audioId, dataItem: data.item });
    
    // audioRefとaudioElementが利用可能か確認
    if (!audioRef.current || !audioRef.current.audio || !audioRef.current.audio.current) {
      console.warn('[handlePlay] audioRefが利用できません。少し待ってから再試行します');
      setTimeout(() => {
        handlePlay();
      }, 100);
      return;
    }
    
    const audioElement = audioRef.current.audio.current;
    
    // console.log('[handlePlay] audioElementの状態:', {
    //   src: audioElement.src,
    //   readyState: audioElement.readyState,
    //   networkState: audioElement.networkState,
    //   paused: audioElement.paused
    // });
    
    // 再生準備フラグを設定
    setIsReady(true);
    
    // 再生を開始（canplayイベントを待つ）
    const tryPlay = () => {
      if (audioElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        // 既に読み込まれている場合は即座に再生
        // console.log('[handlePlay] 既に読み込まれています。再生を開始します');
        audioElement.play().then(() => {
          // console.log('[handlePlay] 再生が開始されました');
          if (audioId) {
            playAudio(audioId);
          }
        }).catch(err => {
          console.error('[handlePlay] 再生エラー:', err);
        });
      } else {
        // まだ読み込まれていない場合は、canplayイベントを待つ
        // console.log('[handlePlay] 読み込みを開始します');
        
        const handleCanPlay = () => {
          // console.log('[handlePlay] canplayイベントが発火しました');
          audioElement.removeEventListener('canplay', handleCanPlay);
          audioElement.removeEventListener('error', handleError);
          
          audioElement.play().then(() => {
            // console.log('[handlePlay] 再生が開始されました');
            if (audioId) {
              playAudio(audioId);
            }
          }).catch(err => {
            console.error('[handlePlay] 再生エラー:', err);
          });
        };
        
        const handleError = (e) => {
          audioElement.removeEventListener('canplay', handleCanPlay);
          audioElement.removeEventListener('error', handleError);
          console.error('[handlePlay] 読み込みエラー:', e);
        };
        
        audioElement.addEventListener('canplay', handleCanPlay);
        audioElement.addEventListener('error', handleError);
        
        // load()を呼んで読み込みを開始
        audioElement.load();
      }
    };
    
    tryPlay();
    
    if (audioId) {
      playAudio(audioId);
    }
  };

  const handlePause = () => {
    if (audioId) {
      pauseAudio(audioId);
    }
  };

  const handleEnded = () => {
    if (audioId) {
      stopAudio(audioId);
    }
  };

  if (!(data && data.item)) return null;

  // srcを常に設定し、preload='none'で制御する
  // preload='none'なら、srcが設定されていてもリソースはダウンロードされない
  const displaySrc = data.item;

  return (
    <Box sx={{ width:'auto', maxWidth:'500px', margin:'2rem auto' }}>
      <AudioPlayer
        ref={audioRef}
        src={displaySrc}
        autoPlay={false}
        autoPlayAfterSrcChange={false}
        layout='stacked'
        preload='none'
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
      />
    </Box>
  )
}

let Movie = (data)=>{
  if (!(data && data.item)) return;

  return (
    <Box p='1rem 0' sx={{ 'div':{margin:'auto'} }}>
      <ReactPlayer
        style={{ maxWidth:'100%', height:'auto' }}
        controls
        url={ data.item }
      />
    </Box>
  )
}

let File = (data)=>{
  if (!(data && data.item)) return;

  // ファイル拡張子からアイコンと表示名を決定
  const getFileInfo = (url) => {
    // URLから拡張子を取得（クエリパラメータを除去）
    let extension = '';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop();
      extension = fileName.split('.').pop()?.toLowerCase() || '';
    } catch {
      // URLが相対パスの場合
      const fileName = url.split('/').pop();
      extension = fileName.split('.').pop()?.toLowerCase() || '';
    }
    
    // console.log('File URL:', url);
    // console.log('File extension:', extension);
    
    switch (extension) {
      case 'pdf':
        return { icon: '📄', name: 'PDFファイル' };
      case 'doc':
      case 'docx':
        return { icon: '📝', name: 'Wordファイル' };
      case 'xls':
      case 'xlsx':
        return { icon: '📊', name: 'Excelファイル' };
      case 'ppt':
      case 'pptx':
        return { icon: '📋', name: 'PowerPointファイル' };
      case 'zip':
      case 'rar':
        return { icon: '📦', name: '圧縮ファイル' };
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
        return { icon: '🖼️', name: '画像ファイル' };
      case 'mp3':
      case 'wav':
      case 'm4a':
      case 'aac':
        return { icon: '🎵', name: '音声ファイル' };
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
        return { icon: '🎬', name: '動画ファイル' };
      case 'txt':
        return { icon: '📄', name: 'テキストファイル' };
      case 'csv':
        return { icon: '📊', name: 'CSVファイル' };
      default:
        // console.log('Unknown extension, using default');
        return { icon: '📎', name: '添付ファイル' };
    }
  };

  const fileInfo = getFileInfo(data.item);

  return (
    <Box p='1rem 0' sx={{ 'div':{margin:'auto'} }}>
      <a 
        href={ data.item } 
        target="_blank"
        rel="noopener noreferrer"
        style={{ 
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s ease',
          color: '#1976d2',
          fontWeight: '500'
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = '#e3f2fd';
          e.target.style.borderColor = '#1976d2';
          e.target.style.transform = 'translateY(-1px)';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = '#f5f5f5';
          e.target.style.borderColor = '#e0e0e0';
          e.target.style.transform = 'translateY(0)';
        }}
      >
        <span style={{ marginRight: '8px', fontSize: '16px' }}>{fileInfo.icon}</span>
        <span style={{ marginRight: '8px' }}>{fileInfo.name}</span>
        <DownloadForOfflineIcon 
          style={{ 
            verticalAlign:'middle',
            fontSize: '18px'
          }} 
        />
      </a>
    </Box>
  )
}

let MultiLineBody = ({body})=>{
  if (!body) return null;

  // URLを正規表現で検出してReactコンポーネントに変換
  const urlRegex = /((h?)(ttps?:\/\/[a-zA-Z0-9.\-_@:/~?%&;=+#',()*!]+))/g;
  
  const processText = (text) => {
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = urlRegex.exec(text)) !== null) {
      // URL前のテキストを追加
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // URLを取得
      const fullUrl = match[0];
      const href = match[3];
      const url = 'h' + href;
      
      // リンクコンポーネントを追加
      parts.push(
        <a 
          key={`url-${match.index}`}
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{
            color: '#1976d2',
            textDecoration: 'none',
            wordBreak: 'break-all',
            borderBottom: '1px solid #90caf9',
            paddingBottom: '1px',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = '#0d47a1';
            e.target.style.borderBottomColor = '#1976d2';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = '#1976d2';
            e.target.style.borderBottomColor = '#90caf9';
          }}
        >
          {fullUrl}
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // 残りのテキストを追加
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };

  let texts = body.split(/\r\n|\n/).map((line, lineIndex)=>{
    return (
      <React.Fragment key={lineIndex}>
        {processText(line)}
        <br />
      </React.Fragment>
    )
  })

  return <>{texts}</>
}

export { Sound, Movie, File, MultiLineBody }
